export interface ShippoConfiguration {
  /**
   * ShippoConfiguration ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /** @readonly */
  revision?: string | null;
  /**
   * Date and time the ShippoConfiguration was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the ShippoConfiguration was last updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Associated delivery region ID.
   * @format GUID
   * @immutable
   * @deprecated Associated delivery region ID.
   * @replacedBy delivery_region_ids
   * @targetRemovalDate 2025-06-01
   */
  deliveryRegionId?: string | null;
  /**
   * Settings of USPS domestic services.
   * @maxSize 3
   */
  domesticServices?: DomesticServiceSettings[];
  /**
   * Settings of USPS international services.
   * @maxSize 3
   */
  internationalServices?: InternationalServiceSettings[];
  /** Package type. */
  packageType?: PackageTypeWithLiterals;
  /** Package details. */
  packageDetails?: PackageDetails;
  /**
   * Location ID from OS location service.
   * @format GUID
   */
  osLocationId?: string | null;
  /**
   * Ship from address materialized from OS location.
   * @readonly
   */
  shipFromAddress?: Address;
  /**
   * This shippo configuration is active for the following delivery regions.
   * @maxSize 50
   * @format GUID
   * @immutable
   */
  deliveryRegionIds?: string[];
  /**
   * Backup weight.
   * @decimalValue options { gte:0, maxScale:5 }
   */
  backupWeight?: string | null;
  /** Data extensions. */
  extendedFields?: ExtendedFields;
}

export interface DomesticServiceSettings {
  /**
   * DomesticServiceSettings ID.
   * @format GUID
   */
  id?: string | null;
  /** USPS domestic service. */
  service?: DomesticServiceWithLiterals;
  /** Service settings. */
  serviceSettings?: ServiceSettings;
}

export enum DomesticService {
  GROUND_ADVANTAGE = 'GROUND_ADVANTAGE',
  PRIORITY_MAIL = 'PRIORITY_MAIL',
  PRIORITY_MAIL_EXPRESS = 'PRIORITY_MAIL_EXPRESS',
}

/** @enumType */
export type DomesticServiceWithLiterals =
  | DomesticService
  | 'GROUND_ADVANTAGE'
  | 'PRIORITY_MAIL'
  | 'PRIORITY_MAIL_EXPRESS';

export interface ServiceSettings {
  /**
   * Estimated delivery time.
   * @maxLength 500
   */
  estimatedDeliveryTime?: string | null;
  /** Handling fee. */
  handlingFee?: HandlingFee;
  /**
   * Amount above which free delivery is offered.
   * @decimalValue options { gte:0, maxScale:6 }
   */
  freeDeliveryMinimumAmount?: string | null;
}

export interface HandlingFee {
  /**
   * Value that will be used to calculate the fee. For example, percentage fee with value 5% to calculate the fee.
   * @decimalValue options { gte:0, maxScale:4 }
   */
  value?: string;
  /** How to calculate the fee: fixed amount or by percentage. */
  calculationType?: CalculationTypeWithLiterals;
}

export enum CalculationType {
  FIXED = 'FIXED',
  PERCENTAGE = 'PERCENTAGE',
}

/** @enumType */
export type CalculationTypeWithLiterals =
  | CalculationType
  | 'FIXED'
  | 'PERCENTAGE';

export interface InternationalServiceSettings {
  /**
   * InternationalServiceSettings ID.
   * @format GUID
   */
  id?: string | null;
  /** USPS international service. */
  service?: InternationalServiceWithLiterals;
  /** Service settings. */
  serviceSettings?: ServiceSettings;
}

export enum InternationalService {
  FIRST_CLASS_PACKAGE_INTERNATIONAL = 'FIRST_CLASS_PACKAGE_INTERNATIONAL',
  PRIORITY_MAIL_INTERNATIONAL = 'PRIORITY_MAIL_INTERNATIONAL',
  PRIORITY_MAIL_EXPRESS_INTERNATIONAL = 'PRIORITY_MAIL_EXPRESS_INTERNATIONAL',
}

/** @enumType */
export type InternationalServiceWithLiterals =
  | InternationalService
  | 'FIRST_CLASS_PACKAGE_INTERNATIONAL'
  | 'PRIORITY_MAIL_INTERNATIONAL'
  | 'PRIORITY_MAIL_EXPRESS_INTERNATIONAL';

export enum PackageType {
  CUSTOM = 'CUSTOM',
  FLAT_RATE_ENVELOPE = 'FLAT_RATE_ENVELOPE',
  PADDED_FLAT_RATE_ENVELOPE = 'PADDED_FLAT_RATE_ENVELOPE',
  SMALL_FLAT_RATE_BOX = 'SMALL_FLAT_RATE_BOX',
  MEDIUM_FLAT_RATE_BOX_1 = 'MEDIUM_FLAT_RATE_BOX_1',
  MEDIUM_FLAT_RATE_BOX_2 = 'MEDIUM_FLAT_RATE_BOX_2',
  LARGE_FLAT_RATE_BOX = 'LARGE_FLAT_RATE_BOX',
}

/** @enumType */
export type PackageTypeWithLiterals =
  | PackageType
  | 'CUSTOM'
  | 'FLAT_RATE_ENVELOPE'
  | 'PADDED_FLAT_RATE_ENVELOPE'
  | 'SMALL_FLAT_RATE_BOX'
  | 'MEDIUM_FLAT_RATE_BOX_1'
  | 'MEDIUM_FLAT_RATE_BOX_2'
  | 'LARGE_FLAT_RATE_BOX';

export interface PackageDetails {
  /**
   * Package name. Must be set when package type is CUSTOM.
   * For other package types, it is read-only, and an exception will be thrown
   * if it set when creating a ShippoConfiguration.
   * @maxLength 255
   */
  name?: string | null;
  /**
   * Package dimensions. Can only be set when package type is CUSTOM.
   * For other package types, it is read-only, and an exception will be thrown
   * if it set when creating a ShippoConfiguration.
   */
  dimensions?: PackageDimensions;
  /**
   * Maximum number of products. Must be set when package type is NOT CUSTOM.
   * Otherwise, if the package type is CUSTOM, this field cannot be set and an
   * exception will be thrown if it is set when creating a ShippoConfiguration.
   */
  maxNumberOfProducts?: string | null;
}

export interface PackageDimensions {
  /**
   * Package length.
   * @decimalValue options { gte:0, maxScale:3 }
   */
  length?: string;
  /**
   * Package width.
   * @decimalValue options { gte:0, maxScale:3 }
   */
  width?: string;
  /**
   * Package height.
   * @decimalValue options { gte:0, maxScale:3 }
   */
  height?: string;
}

/** Physical address */
export interface Address {
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @maxLength 50
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 50
   */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 50
   */
  postalCode?: string | null;
  /** Street address. */
  streetAddress?: StreetAddress;
  /**
   * Main address line (usually street name and number).
   * @maxLength 150
   */
  addressLine?: string | null;
  /**
   * Free text providing more detailed address info. Usually contains apt, suite, floor.
   * @maxLength 100
   */
  addressLine2?: string | null;
  /** Geocode object containing latitude and longitude coordinates. */
  geocode?: AddressLocation;
  /**
   * Country's full name.
   * @readonly
   */
  countryFullname?: string | null;
  /**
   * Subdivision full-name.
   * @readonly
   */
  subdivisionFullname?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateShippoConfigurationRequest {
  /** ShippoConfiguration to be created. */
  shippoConfiguration: ShippoConfiguration;
  /** @decimalValue options { gte:0, maxScale:6 } */
  backupRate?: string | null;
}

export interface CreateShippoConfigurationResponse {
  /** The created ShippoConfiguration. */
  shippoConfiguration?: ShippoConfiguration;
}

export interface GetShippoConfigurationRequest {
  /**
   * ID of the ShippoConfiguration to retrieve.
   * @format GUID
   */
  shippoConfigurationId: string;
}

export interface GetShippoConfigurationResponse {
  /** The requested ShippoConfiguration. */
  shippoConfiguration?: ShippoConfiguration;
}

export interface UpdateShippoConfigurationRequest {
  /** ShippoConfiguration to be updated, may be partial. */
  shippoConfiguration: ShippoConfiguration;
}

export interface UpdateShippoConfigurationResponse {
  /** Updated ShippoConfiguration. */
  shippoConfiguration?: ShippoConfiguration;
}

export interface DeleteShippoConfigurationRequest {
  /**
   * ID of the ShippoConfiguration to delete.
   * @format GUID
   */
  shippoConfigurationId: string;
}

export interface DeleteShippoConfigurationResponse {}

export interface QueryShippoConfigurationsRequest {
  /** WQL expression. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryShippoConfigurationsResponse {
  /** List of ShippoConfigurations. */
  shippoConfigurations?: ShippoConfiguration[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated ShippoConfiguration. */
  shippoConfiguration?: ShippoConfiguration;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface AddDeliveryRegionRequest {
  /**
   * ID of the ShippoConfiguration to add the delivery region to.
   * @format GUID
   */
  shippoConfigurationId: string;
  /**
   * ID of the delivery region to add.
   * @format GUID
   */
  deliveryRegionId: string;
  /** Revision of the ShippoConfiguration. */
  revision?: string | null;
}

export interface AddDeliveryRegionResponse {
  /** The updated ShippoConfiguration. */
  shippoConfiguration?: ShippoConfiguration;
}

export interface RemoveDeliveryRegionRequest {
  /**
   * ID of the ShippoConfiguration to remove the delivery region from.
   * @format GUID
   */
  shippoConfigurationId: string;
  /**
   * ID of the delivery region to remove.
   * @format GUID
   */
  deliveryRegionId: string;
  /** Revision of the ShippoConfiguration. */
  revision?: string | null;
}

export interface RemoveDeliveryRegionResponse {
  /** The updated ShippoConfiguration, or empty if the ShippoConfiguration was deleted. */
  shippoConfiguration?: ShippoConfiguration;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
